import { WorkOrderStatus } from "features/WorkOrders/constants/work-order.constants";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import type { WorkOrder } from "features/WorkOrders/types/work-order.types";

/** When true, show Hold / Resume in overview + list. Backend may already support them. */
export const WORK_ORDER_UI_HOLD_RESUME_ENABLED = true;

/** When true, show Close after completed. Backend may already support it. */
export const WORK_ORDER_UI_CLOSE_ENABLED = false;

/** Normalizes legacy API status values for lifecycle UI checks. */
export function normalizedWorkOrderStatus(
  status: string | null | undefined,
): string {
  const s = String(status ?? "").trim();
  if (s === "DRAFT") return WorkOrderStatus.NEW;
  if (s === "ASSIGNED") return WorkOrderStatus.NEW;
  return s;
}

const WO_CANCELLABLE_STATUSES: ReadonlySet<string> = new Set([
  WorkOrderStatus.NEW,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.ON_HOLD,
]);

export type WorkOrderLifecycleVisibility = {
  start: boolean;
  hold: boolean;
  resume: boolean;
  /** IN_PROGRESS → PENDING_COMPLETION_APPROVAL (replaces old single-step complete) */
  submitCompletion: boolean;
  /** PENDING_COMPLETION_APPROVAL → READY_FOR_BILLING */
  approveForBilling: boolean;
  /** PENDING_COMPLETION_APPROVAL → IN_PROGRESS */
  rejectCompletion: boolean;
  /** READY_FOR_BILLING → COMPLETED */
  submitBilling: boolean;
  close: boolean;
  cancel: boolean;
};

/** Which lifecycle actions are allowed for this row (permissions + status). */
export function getWorkOrderLifecycleVisibility(
  workOrder: Pick<WorkOrder, "status">,
  can: (permission: string) => boolean,
): WorkOrderLifecycleVisibility {
  const raw = String(workOrder.status ?? "").trim();
  const normalized = normalizedWorkOrderStatus(workOrder.status);
  return {
    start:
      can(Permissions.WORK_ORDER.START) &&
      normalized === WorkOrderStatus.NEW,
    hold:
      WORK_ORDER_UI_HOLD_RESUME_ENABLED &&
      can(Permissions.WORK_ORDER.HOLD) &&
      raw === WorkOrderStatus.IN_PROGRESS,
    resume:
      WORK_ORDER_UI_HOLD_RESUME_ENABLED &&
      can(Permissions.WORK_ORDER.RESUME) &&
      raw === WorkOrderStatus.ON_HOLD,
    submitCompletion:
      can(Permissions.WORK_ORDER.COMPLETE) &&
      raw === WorkOrderStatus.IN_PROGRESS,
    approveForBilling:
      can(Permissions.WORK_ORDER.APPROVE) &&
      raw === WorkOrderStatus.PENDING_COMPLETION_APPROVAL,
    rejectCompletion:
      can(Permissions.WORK_ORDER.REJECT) &&
      raw === WorkOrderStatus.PENDING_COMPLETION_APPROVAL,
    submitBilling:
      can(Permissions.WORK_ORDER.SUBMIT_BILLING) &&
      raw === WorkOrderStatus.READY_FOR_BILLING,
    close:
      WORK_ORDER_UI_CLOSE_ENABLED &&
      can(Permissions.WORK_ORDER.CLOSE) &&
      raw === WorkOrderStatus.COMPLETED,
    cancel:
      can(Permissions.WORK_ORDER.CANCEL) &&
      WO_CANCELLABLE_STATUSES.has(normalized),
  };
}

export function workOrderLifecycleAnyVisible(
  v: WorkOrderLifecycleVisibility,
): boolean {
  return (
    v.start ||
    v.hold ||
    v.resume ||
    v.submitCompletion ||
    v.approveForBilling ||
    v.rejectCompletion ||
    v.submitBilling ||
    v.close ||
    v.cancel
  );
}
